/* eslint no-shadow: 0 */
import { createSelector } from "reselect";
import sortBy from "lodash/sortBy";
import numeral from "numeral";
import differenceInCalendarYears from "date-fns/difference_in_calendar_years";
import differenceInCalendarMonths from "date-fns/difference_in_calendar_months";
import { languages, pollutantsInfo, dictionary, allowList, MONTHS, DEFAULT_YEAR } from "./constants";
import moment from 'moment';


const loading = ({ emissionsDemo }) => emissionsDemo.products.loading;
const error = ({ emissionsDemo }) => emissionsDemo.products.error;
const products = ({ emissionsDemo }) => emissionsDemo.products.data;
const productId = ({ emissionsDemo }) => emissionsDemo.state.productId;
const city = ({ emissionsDemo }) => emissionsDemo.state.city;
const productType = ({ emissionsDemo }) => emissionsDemo.state.productType;
const pollutant = ({ emissionsDemo }) => emissionsDemo.state.pollutant;
const sector = ({ emissionsDemo }) => emissionsDemo.state.sector;
const year = ({ emissionsDemo }) => emissionsDemo.state.year;
const month = ({ emissionsDemo }) => emissionsDemo.state.month;
const comparingYear = ({ emissionsDemo }) => emissionsDemo.state.comparingYear;
const comparingMonth = ({ emissionsDemo }) => emissionsDemo.state.comparingMonth;
const basemap = ({ emissionsDemo }) => emissionsDemo.state.basemap;
const labels = ({ emissionsDemo }) => emissionsDemo.state.labels;
const language = ({ emissionsDemo }) => emissionsDemo.state.language;
const viewport = ({ emissionsDemo }) => emissionsDemo.state.viewport;
const timeseries = ({ emissionsDemo }) => emissionsDemo.state.timeset;

/**
 * Get the valid products for the emissions demo
 */
export const getValidProducts = createSelector([loading, error, products], (
  loading,
  error,
  products,

) => {
  if (loading || error) return [];

  return products.filter(
    product => product.productType.code.toLowerCase().includes('inventory')
      && ((product.id === 'd5409e1d-1317-4519-b312-9f4c2ca48118')
      || (product.id === 'd3916857-eb99-4c8f-b346-340829779057'))
  );
});

/**
 * Get timesets for pollutants in emissions demo
 */
export const getTimeSeries = createSelector([timeseries], (
  timeseries
) => {
  if (!timeseries) return null;

  const limItedResults = timeseries.filter(({ t }) => t.includes(DEFAULT_YEAR));

  const formatDate = limItedResults.map(({ t, value }) => ({
    label: moment(t).format('MMM'),
    name: moment(t).format('MMM'),
    value: (value * 1000).toFixed(2)
  }));

  return formatDate;
});

/**
 * Get the selected product
 */
export const getSelectedProduct = createSelector(
  [getValidProducts, productId],
  (products, productId) => {
    if (products.length && productId) {
      return products.find(product => product.id === productId);
    }
    return null;
  },
);

/**
 * Get the dictionary of pollutants/sectors
 */
export const getPollutantsDict = createSelector(
  [getSelectedProduct],
  (product) => {
    if (!product) return {};

    const res = {};
    product.variables.forEach((variable) => {
      const split = variable.split('_');
      const pollutant = split[0];
      const sector = split[1];
      if (res[pollutant]) {
        res[pollutant].push(sector);
      } else {
        res[pollutant] = [sector];
      }
    });

    return res;
  },
);

/**
 * Get the area options
 */
export const getAreas = createSelector(
  [getValidProducts],
  products => sortBy(
    products.map(product => ({
      label: product.domain.name,
      value: product.domain.code,
    })),
    ['label'],
  ),
);

/**
 * Get the selected area option
 */
export const getSelectedArea = createSelector(
  [getAreas, getSelectedProduct],
  (areas, product) => {
    if (!product) return null;
    return areas.find(area => area.value === product.domain.code);
  },
);

/**
 * Get the pollutants options
 */
export const getPollutants = createSelector(
  [getPollutantsDict],
  pollutantsDict => sortBy(Object.keys(pollutantsDict).map((pollutant) => {
    const label = pollutantsInfo[pollutant]
      ? pollutantsInfo[pollutant].displayName
      : pollutant;
    const description = pollutantsInfo[pollutant]
      ? pollutantsInfo[pollutant].description
      : pollutant;
    return { label, value: pollutant, description };
  }), ['label']),
);

/**
 * Get the selected pollutant option
 */
export const getSelectedPollutant = createSelector(
  [getPollutants, pollutant],
  (pollutants, pollutant) => {
    if (pollutant && pollutants.find(p => p.value === pollutant.value)) {
      return {
        label: pollutant.label,
        value: pollutant.value,
        description: pollutant.description,
      };
    }
    return null;
  }
);

/**
 * Get the sectors options
 */
export const getSectors = createSelector([getPollutantsDict, pollutant], (
  pollutantsDict,
  pollutant,
) => {
  if (!pollutant || !pollutant.value || !pollutantsDict[pollutant.value]) return [];
  const sectors = pollutantsDict[pollutant.value]
  const sectorsFiltered = sectors.filter(sector => sector.toLowerCase() === 'industry' || sector.toLowerCase() === 'total' || sector.toLowerCase() === 'residential');

  return sortBy(
    sectorsFiltered.map(sector => ({
      label: sector,
      value: sector,
    })),
    ['label'],
  );
});

/**
 * Get the selected sector option
 */
export const getSelectedSector = createSelector(
  [getSectors, sector],
  (sectors, sector) => {
    const sectorResult = sectors.find(s => s.value === sector);
    if (sectorResult) return sectorResult;
    return null;
  }
);

/**
 * Get the type of product options
 */
export const getProductTypes = createSelector(
  [getValidProducts],
  products => sortBy(
    products.map(product => ({
      label: product.productType.name.split([' '], 1).join(' '),
      value: product.id
    })),
    ['label'],
  )
);


/**
 * Get the selected productType
 */
export const getSelectedProductType = createSelector(
  [getProductTypes, productType],
  (products, productType) => {
    if (products && products.length && productType) {
      const productTypeSelected = products.find(product => product.value === productType);
      return productTypeSelected;
    }

    const productTypeCallback = products && products[0];

    if (productTypeCallback) return productTypeCallback;

    return null;
  },
);

/**
 * Get the selected city
 */
export const getSelectedCity = createSelector(
  [city],
  (city) => {
    if (city) {
      return city
    }
    return null;
  },
);



/*
 * Get the years options
 */
export const getYears = createSelector([getSelectedProduct], (product) => {
  if (!product) return [];

  const range = [new Date(product.tMax), new Date(product.tMin)];
  const diff = differenceInCalendarYears(...range);
  const startYear = range[1].getUTCFullYear();

  return new Array(diff + 1)
    .fill(null)
    .map((_, i) => ({ label: `${startYear + i}`, value: startYear + i }));
});

/**
 * Get the selected year option
 */
export const getSelectedYear = createSelector(
  [getYears, year],
  (years, year) => (year && years.find(y => y.value === year)
    ? { label: `${year}`, value: year }
    : null),
);

/**
 * Get the months options
 */
export const getMonths = createSelector(
  [getSelectedProduct, getSelectedYear],
  (product, year) => {
    if (!product || !year) return [];

    const range = [
      new Date(product.tMax),
      new Date(Date.UTC(year.value, 0, 1)),
    ];


    // This condition can occur when restoring a previous state
    if (range[0] < range[1]) return [];

    const diff = differenceInCalendarMonths(...range);

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({ label: MONTHS[i], value: i }));
  },
);

/**
 * Get the selected month option
 */
export const getSelectedMonth = createSelector(
  [getMonths, month],
  (months, month) => (month !== null && months.find(m => m.value === month)
    ? { label: MONTHS[month], value: month }
    : null),
);

/**
 * Get the selected language option
 */
export const getSelectedLanguage = createSelector(
  [language],
  language => languages.find(l => l.value === language),
);

/**
 * Get the comparing years options
 */
export const getComparingYears = createSelector(
  [getSelectedProduct, getSelectedYear, getSelectedMonth],
  (product, year, month) => {
    if (!product || !year || !month) return [];

    const range = [
      new Date(product.tMax),
      new Date(Date.UTC(year.value, month.value, 1)),
    ];

    // This condition can occur when restoring a previous state
    if (range[0] < range[1]) return [];

    const diff = differenceInCalendarYears(...range);
    const startYear = range[1].getUTCFullYear();

    return new Array(diff + 1)
      .fill(null)
      .map((_, i) => ({ label: `${startYear + i}`, value: startYear + i }));
  },
);

/**
 * Get the selected comparing year option
 */
export const getSelectedComparingYear = createSelector(
  [getComparingYears, comparingYear],
  (years, year) => (year && years.find(y => y.value === year)
    ? { label: `${year}`, value: year }
    : null),
);

/**
 * Get the comparing months options
 */
export const getComparingMonths = createSelector(
  [getSelectedProduct, getSelectedYear, getSelectedMonth],
  (product, year, month) => {
    if (!product || !year || !month || month.value === 11) return [];

    const range = [
      new Date(product.tMax),
      new Date(Date.UTC(year.value, month.value, 1)),
    ];


    // This condition can occur when restoring a previous state
    if (range[0] < range[1]) return [];

    const diff = differenceInCalendarMonths(...range);

    const months = new Array(diff)
      .fill(null)
      .map((_, i) => ({
        label: MONTHS[month.value + 1 + i],
        value: month.value + 1 + i,
      }));

    return months.filter(month => allowList.includes(month.label))
  }
);

/**
 * Get the selected comparing month option
 */
export const getSelectedComparingMonth = createSelector(
  [getComparingMonths, comparingMonth],
  (months = [], month) => (month !== null && months.find(m => m.value === month)
    ? { label: MONTHS[month], value: month }
    : null),
);


/**
 * Return the bounds of the map
 */
export const getBounds = createSelector([city, getSelectedArea], (city, area) => {
  if (city) {
    const coords = dictionary[city.label]
    return [
      [coords.latMax, coords.lonMin],
      [coords.latMin, coords.lonMax]
    ];
  }
  if (!area) return null;

  const coords = dictionary[area.label]
  return [
    [coords.latMax, coords.lonMin],
    [coords.latMin, coords.lonMax]
  ];
});

/**
 * Get the URL of the tiles
 */
export const getTilesUrl = createSelector(
  [
    productId,
    getSelectedPollutant,
    getSelectedSector,
    getSelectedYear,
    getSelectedMonth,
  ],
  (productId, pollutant, sector, year, month) => {
    if (!productId || !pollutant || !sector || !year || !month) return null;
    try {
      const date = new Date(Date.UTC(year.value, month.value, 1)).toISOString();
      const retina = window.devicePixelRatio > 1 ? '@2x' : '';
      return `${process.env.REACT_APP_API_URL}/product/${productId}/tile/{z}/{x}/{y}${retina}.png?variable=${pollutant.value}_${sector.value}&t=${date}`;
    } catch (err) {
      // We can enter this case if the date was restored from the URL
      return null;
    }
  },
);

/**
 * Get the URL of the "comparing" tiles
 */
export const getComparingTilesUrl = createSelector(
  [
    productId,
    getSelectedPollutant,
    getSelectedSector,
    getSelectedComparingYear,
    getSelectedComparingMonth,
  ],
  (productId, pollutant, sector, year, month) => {
    if (!productId || !pollutant || !sector || !year || !month) return null;

    try {
      const date = new Date(Date.UTC(year.value, month.value, 1)).toISOString();
      const retina = window.devicePixelRatio > 1 ? '@2x' : '';
      return `${process.env.REACT_APP_API_URL}/product/${productId}/tile/{z}/{x}/{y}${retina}.png?variable=${pollutant.value}_${sector.value}&t=${date}`;
    } catch (err) {
      // We can enter this case if the date was restored from the URL
      return null;
    }
  },
);

const legendItems = [
  { color: 'rgb(0, 0, 143)' },
  { color: 'rgb(0, 0, 255)' },
  { color: 'rgb(0, 127, 255)' },
  { color: 'rgb(0, 255, 255)' },
  { color: 'rgb(127, 255, 127)' },
  { color: 'rgb(255, 255, 0)' },
  { color: 'rgb(255, 127, 0)' },
  { color: 'rgb(255, 0, 0)' },
  { color: 'rgb(127, 0, 0)' },
];

/**
 * Get legend values
 */
export const getLegendValues = createSelector(
  [getSelectedProduct, getSelectedPollutant, getSelectedSector],
  (product, pollutant, sector) => {
    if (!product || !pollutant || !sector) return [];

    const legendValue = product.defaultRenderProps[`${pollutant.value}_${sector.value}`];
    let unit = legendValue.units;
    const { length } = legendItems;
    const res = legendItems.map((item, index) => {
      let value = (legendValue.valueMin + legendValue.valueMax)
        / length
        * (index + 1);
      if (value < 0.01) {
        value = (value * 1000).toFixed(2);
        unit = 'Mg/month/cell';
      } else if (value >= 1000) {
        value = numeral(Math.floor(value)).format('0.0a');
      } else if (value < 10) {
        value = value.toFixed(1);
      } else {
        value = Math.round(value);
      }
      return {
        ...item,
        name: `${pollutant.value}_${sector.value}_${index}`,
        value,
        unit
      };
    });
    return res;
  },
);

/**
 * Get the selected basemap ID
 */
export const getSelectedBasemap = createSelector(
  [basemap],
  basemap => basemap,
);

/**
 * Get the selected labels ID
 * NOTE: the id can evaluate to "none" meaning no
 * labels are displayed
 */
export const getSelectedLabels = createSelector([labels], labels => labels);

export const getViewport = createSelector([viewport], viewport => viewport);



// WEBPACK FOOTER //
// ./src/components/emissions-demo/selectors.js